import { Link } from 'react-router-dom'
import { cn } from '@/shared/lib/cn'

/**
 * Bento grid (§6.7.4). Locked spans:
 *   - mobile: stack 1-col
 *   - sm:    2-col
 *   - lg:    6-col base, tiles use {1|2|3} col-span and {1|2} row-span
 */
export function BentoGrid({
  children,
  className,
}: {
  children: React.ReactNode
  className?: string
}) {
  return (
    <div
      className={cn(
        'grid auto-rows-[minmax(180px,auto)] gap-4 sm:gap-5',
        'grid-cols-1 sm:grid-cols-2 lg:grid-cols-6',
        className,
      )}
    >
      {children}
    </div>
  )
}

type Span = 1 | 2 | 3
type RowSpan = 1 | 2

interface BentoTileProps {
  /** col-span on lg+ (default 2). */
  col?: Span
  /** col-span on sm+ (default 1). */
  smCol?: 1 | 2
  /** row-span on lg+ (default 1). */
  row?: RowSpan
  /** Adds the orange-bordered "featured" treatment. */
  featured?: boolean
  /** Wrap the tile in a router Link instead of an article. */
  href?: string
  className?: string
  children: React.ReactNode
}

const colMap: Record<Span, string> = {
  1: 'lg:col-span-1',
  2: 'lg:col-span-2',
  3: 'lg:col-span-3',
}
const smColMap = {
  1: 'sm:col-span-1',
  2: 'sm:col-span-2',
}
const rowMap: Record<RowSpan, string> = {
  1: 'lg:row-span-1',
  2: 'lg:row-span-2',
}

/**
 * One tile inside a `<BentoGrid>`. Picks rounded-2xl, soft hover lift, the
 * inner padding ladder, and the locked col/row span values.
 */
export function BentoTile({
  col = 2,
  smCol = 1,
  row = 1,
  featured = false,
  href,
  className,
  children,
}: BentoTileProps) {
  const baseClasses = cn(
    'group relative isolate overflow-hidden rounded-2xl border bg-card p-6 sm:p-7',
    'card-hover',
    featured
      ? 'border-primary/40 bg-gradient-to-br from-card via-card to-primary/5'
      : 'border-border',
    smColMap[smCol],
    colMap[col],
    rowMap[row],
    href && 'cursor-pointer',
    className,
  )

  if (href) {
    return (
      <Link to={href} className={baseClasses}>
        {children}
      </Link>
    )
  }

  return <article className={baseClasses}>{children}</article>
}
